import React from 'react';
import { Box, Typography, useTheme } from '@mui/material';
import { font } from '../../utils/fontHelper';

const CANVAS_BG = '#0b0b09';
const COLLAGE_URL = '/images/collage.jpg';

/**
 * Import flow loading state with animated bracket logo.
 * Features:
 * - Blurred movie poster collage background with parallax motion
 * - Glass container with purple gradient border glow
 * - Pulsing bracket logo with purple-pink gradient
 */
const ImportMoviesLoadingOverlay: React.FC<{ statusText: string }> = ({
  statusText,
}) => {
  const theme = useTheme();

  return (
    <Box
      sx={{
        position: 'absolute',
        inset: 0,
        zIndex: 4,
        borderRadius: 2,
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: { xs: 280, sm: 300 },
        height: '100%',
        bgcolor: CANVAS_BG,
      }}
    >
      {/* Background stack with movie poster collage */}
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          overflow: 'hidden',
          bgcolor: CANVAS_BG,
        }}
      >
        {/* Wide collage group with blur and parallax */}
        <Box
          sx={{
            position: 'absolute',
            left: { xs: '-10%', sm: '-7%' },
            top: '10%',
            width: { xs: '165%', sm: '155%' },
            height: '92%',
          }}
        >
          {/* Layer 1: full-bleed collage with heavy blur */}
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '72%',
              opacity: 0.5,
              filter: 'blur(18px)',
              '@keyframes importSplashBlurDrift': {
                '0%': { transform: 'translate3d(0, 0, 0) scale(1)' },
                '50%': { transform: 'translate3d(-2.5%, -1.2%, 0) scale(1.03)' },
                '100%': { transform: 'translate3d(0, 0, 0) scale(1)' },
              },
              animation: 'importSplashBlurDrift 26s ease-in-out infinite',
            }}
          >
            <Box
              component="img"
              src={COLLAGE_URL}
              alt=""
              sx={{
                position: 'absolute',
                inset: 0,
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                pointerEvents: 'none',
              }}
            />
          </Box>

          {/* Layer 2: sharper window with parallax motion */}
          <Box
            sx={{
              position: 'absolute',
              left: 0,
              top: '14%',
              width: '100%',
              height: '38%',
              opacity: 0.45,
              overflow: 'hidden',
              pointerEvents: 'none',
            }}
          >
            <Box
              component="img"
              src={COLLAGE_URL}
              alt=""
              sx={{
                position: 'absolute',
                left: 0,
                width: '100%',
                height: '353.41%',
                top: '-52.8%',
                maxWidth: 'none',
                objectFit: 'cover',
                '@keyframes importSplashParallax': {
                  '0%': { transform: 'translate3d(0, 0, 0)' },
                  '100%': { transform: 'translate3d(-3.5%, -8%, 0)' },
                },
                animation: 'importSplashParallax 32s ease-in-out infinite alternate',
              }}
            />
          </Box>
        </Box>

        {/* Dark vignette overlay */}
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            pointerEvents: 'none',
            background:
              'radial-gradient(ellipse at center, rgba(0,0,0,0.3) 0%, rgba(0,0,0,0.85) 100%)',
          }}
        />
      </Box>

      {/* Glass container with gradient border glow */}
      <Box
        sx={{
          position: 'relative',
          zIndex: 2,
          width: { xs: 140, sm: 160 },
          height: { xs: 140, sm: 160 },
          borderRadius: '32px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          // Gradient border using pseudo-element
          '&::before': {
            content: '""',
            position: 'absolute',
            inset: -2,
            borderRadius: '34px',
            padding: '2px',
            background: 'linear-gradient(180deg, rgba(108, 100, 253, 0.5) 0%, rgba(174, 155, 247, 0.3) 50%, rgba(235, 169, 252, 0.5) 100%)',
            WebkitMask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
            WebkitMaskComposite: 'xor',
            maskComposite: 'exclude',
            '@keyframes borderGlow': {
              '0%, 100%': { opacity: 0.6 },
              '50%': { opacity: 1 },
            },
            animation: 'borderGlow 2s ease-in-out infinite',
          },
          // Inner glass background
          bgcolor: 'rgba(20, 10, 40, 0.6)',
          backdropFilter: 'blur(40px)',
          WebkitBackdropFilter: 'blur(40px)',
          boxShadow: '0 8px 32px rgba(108, 100, 253, 0.2), 0 0 60px rgba(174, 155, 247, 0.15), inset 0 1px 0 rgba(255, 255, 255, 0.1)',
          '@keyframes importLogoFloat': {
            '0%': { transform: 'translateY(-4px)' },
            '100%': { transform: 'translateY(4px)' },
          },
          animation: 'importLogoFloat 2.5s ease-in-out infinite alternate',
        }}
      >
        {/* Animated bracket logo with gradient */}
        <Box
          sx={{
            position: 'relative',
            width: { xs: 90, sm: 100 },
            height: { xs: 90, sm: 100 },
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            '@keyframes logoPulse': {
              '0%, 100%': {
                opacity: 0.85,
                filter: 'drop-shadow(0 0 8px rgba(174, 155, 247, 0.4))',
              },
              '50%': {
                opacity: 1,
                filter: 'drop-shadow(0 0 16px rgba(174, 155, 247, 0.7))',
              },
            },
            animation: 'logoPulse 1.8s ease-in-out infinite',
          }}
        >
          {/* Inline SVG with gradient for the bracket logo */}
          <svg width="100%" height="100%" viewBox="0 0 72 72" fill="none" xmlns="http://www.w3.org/2000/svg">
            <defs>
              <linearGradient id="importBracketGradient" x1="36" y1="24" x2="36" y2="49" gradientUnits="userSpaceOnUse">
                <stop offset="0%" stopColor="#6C64FD"/>
                <stop offset="40%" stopColor="#AE9BF7"/>
                <stop offset="100%" stopColor="#EBA9FC"/>
              </linearGradient>
            </defs>
            {/* Right bracket ] */}
            <path d="M49 44H44V39H39V34H44V29H49V24H54V29H59V34H64V39H59V44H54V49H49V44Z" fill="url(#importBracketGradient)"/>
            {/* Left bracket [ */}
            <path d="M19 44H14V39H9V34H14V29H19V24H24V29H29V34H34V39H29V44H24V49H19V44Z" fill="url(#importBracketGradient)"/>
          </svg>
        </Box>
      </Box>

      <Typography
        variant="body2"
        sx={{
          position: 'relative',
          zIndex: 2,
          mt: 3,
          px: 2,
          textAlign: 'center',
          fontFamily: font(),
          fontWeight: 500,
          color: 'rgba(255, 255, 255, 0.9)',
          maxWidth: 280,
          textShadow: '0 2px 8px rgba(0, 0, 0, 0.5)',
        }}
      >
        {statusText.trim() || 'Working…'}
      </Typography>
    </Box>
  );
};

export default ImportMoviesLoadingOverlay;
